import React from 'react';
import { CARD_LONG as H, CARD_SHORT as W } from '../../catalog';
import { V, T, Svg, Path } from '../primitives';
import { CardFaceProps, DetailGrid, Logo, Photo, Validity, faceBase, faceData, nameStyle, type } from './shared';

const CURVE = 82;     // where the masthead's curved edge meets the sides
const DIP = 22;       // how far the curve dips at the centre
const PHOTO = 64;
const PHOTO_TOP = 62;
const FOOT = 17;

/** Portrait — a vertical lanyard card: curved masthead, round photo at the
 *  centre, the first detail as a badge under the name. */
const PortraitFront: React.FC<CardFaceProps> = (props) => {
  const { design, person, photo, school, validUntil } = props;
  const { palette, details } = faceData(props);
  const [featured, ...rest] = details;
  const ring = PHOTO + 7;
  return (
    <V style={[faceBase(W, H, palette.paper), { alignItems: 'center' }]}>
      <Svg width={W} height={CURVE + DIP + 6} viewBox={`0 0 ${W} ${CURVE + DIP + 6}`} style={{ position: 'absolute', left: 0, top: 0 }}>
        <Path d={`M0 0 H${W} V${CURVE + 3} Q${W / 2} ${CURVE + DIP + 9} 0 ${CURVE + 3} Z`} fill={palette.accent} />
        <Path d={`M0 0 H${W} V${CURVE} Q${W / 2} ${CURVE + DIP + 6} 0 ${CURVE} Z`} fill={palette.primary} />
        <Path d={`M${W * 0.55} 0 H${W} V${CURVE * 0.7} Z`} fill={palette.primaryDeep} opacity={0.45} />
      </Svg>

      {/* Masthead */}
      <V style={{ alignItems: 'center', paddingTop: 10, paddingHorizontal: 12 }}>
        <Logo school={school} size={21} palette={palette} tile round />
        <T style={{ fontSize: 7.3, fontWeight: 700, color: palette.onPrimary, textAlign: 'center', marginTop: 4, letterSpacing: -0.1 }} lines={2}>{school.name}</T>
        <T style={[type.caps, { fontSize: 4.3, color: palette.accent, marginTop: 2, textAlign: 'center' }]} lines={1}>{design.title}</T>
      </V>

      {/* Photo, ringed in white then accent */}
      <V style={{ position: 'absolute', top: PHOTO_TOP, left: (W - ring) / 2, width: ring, height: ring, borderRadius: ring / 2, backgroundColor: palette.accent, alignItems: 'center', justifyContent: 'center' }}>
        <Photo photo={photo} name={person.name} width={PHOTO} height={PHOTO} radius={PHOTO / 2} palette={palette}
          ring={{ width: 2.2, color: palette.paper }} />
      </V>

      {/* Holder */}
      <V style={{ position: 'absolute', top: PHOTO_TOP + ring + 6, left: 0, right: 0, alignItems: 'center', paddingHorizontal: 12 }}>
        <T style={[nameStyle(person.name, 10.4), { color: palette.ink, textAlign: 'center' }]} lines={2}>{person.name}</T>
        {featured && (
          <V style={{ marginTop: 4, paddingHorizontal: 8, paddingVertical: 2.4, borderRadius: 8, backgroundColor: palette.wash, flexDirection: 'row', alignItems: 'center' }}>
            <T style={[type.label, { color: palette.inkMuted, marginRight: 3 }]}>{featured.label}</T>
            <T style={[type.value, { fontSize: 6, color: palette.primaryText }, featured.mono ? type.mono : {}]} lines={1}>{featured.value}</T>
          </V>
        )}
        <V style={{ marginTop: 7, alignSelf: 'stretch', paddingHorizontal: 2 }}>
          <DetailGrid details={rest} palette={palette} align="center" gap={3.6} />
        </V>
      </V>

      {/* Footer */}
      <V style={{ position: 'absolute', left: 0, right: 0, bottom: FOOT, height: 1.5, backgroundColor: palette.accent }} />
      <V style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: FOOT, backgroundColor: palette.primary, alignItems: 'center', justifyContent: 'center' }}>
        <Validity validUntil={validUntil} color={palette.onPrimary} labelColor={palette.onPrimaryMuted} align="center" />
      </V>
    </V>
  );
};

export default PortraitFront;
